import { useEffect, useState } from 'react';
import { Room, TwilioError } from 'twilio-video';

import { DUPLICATE_IDENTITY_ERROR, HOST_IDENTITY, ROOM_COMPLETED_ERROR } from '../../constants';
import useVideoContext from '../useVideoContext/useVideoContext';

/**
 * True when this guest was disconnected but the call is still going, so the same link can bring them
 * back. False for the host (who starts a new call instead), when the host ended the call (the link is
 * dead), and when the link was opened on another device (rejoining here would knock that one off).
 */
export default function useCanRejoin() {
  const { room } = useVideoContext();
  const [canRejoin, setCanRejoin] = useState(false);

  useEffect(() => {
    const onDisconnected = (disconnectedRoom: Room, error?: TwilioError) => {
      const isHost = disconnectedRoom.localParticipant?.identity === HOST_IDENTITY;
      const code = error?.code;
      setCanRejoin(!isHost && code !== ROOM_COMPLETED_ERROR && code !== DUPLICATE_IDENTITY_ERROR);
    };
    const onConnected = () => setCanRejoin(false);
    room.on('disconnected', onDisconnected);
    (room as unknown as NodeJS.EventEmitter).on('connected', onConnected);
    return () => {
      room.off('disconnected', onDisconnected);
      (room as unknown as NodeJS.EventEmitter).off('connected', onConnected);
    };
  }, [room]);

  return canRejoin;
}
